import React, { useContext, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { AssetTabs } from '../../components/assets/AssetTabs';
import { DomainsTable } from '../../components/assets/DomainsTable';
import { AssetsFilters } from '../../components/assets/AssetsFilters';
import { AssetsMetrics } from '../../components/assets/AssetsMetrics';
import { ExportButton, AddAssetButton } from '../../components/assets/AssetActionButtons';
import { useTeams } from '../../contexts/TeamsContext';
import { AssetsApi, Domain, Technology, DomainsListRequest } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { AuthContext } from '../../contexts/AuthContext';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

const parseJsonData = <T extends object>(data: string | null | undefined): T | null => {
  if (!data) return null;
  try {
    // Parse string or return as is if already an object
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    // Ensure the result is treated as the expected type
    return parsed as T;
  } catch (e) {
    console.error('Error parsing data:', e);
    return null;
  }
};

interface FindingsStats {
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
  total?: number;
  [key: string]: any;
}

const Domains = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from URL search params
  const [currentPage, setCurrentPage] = React.useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = React.useState(searchParams.get('type') || 'all');
  const [selectedStatus, setSelectedStatus] = React.useState(searchParams.get('status') || 'all');
  const [selectedSeverity, setSelectedSeverity] = React.useState(searchParams.get('severity') || 'all');
  const [selectedTimeframe, setSelectedTimeframe] = React.useState(searchParams.get('timeframe') || 'all');
  // New risk score filter states
  const [selectedRiskScoreMin, setSelectedRiskScoreMin] = React.useState<string>(searchParams.get('riskScore') || '');
  // const [selectedRiskScoreMax, setSelectedRiskScoreMax] = React.useState<string>('');
  // New findings count filter states
  const [selectedFindingsMin, setSelectedFindingsMin] = React.useState<number | undefined>(
    searchParams.get('findingsMin') ? parseInt(searchParams.get('findingsMin') || '0', 10) : undefined
  );
  const [selectedFindingsMax, setSelectedFindingsMax] = React.useState<number | undefined>(
    searchParams.get('findingsMax') ? parseInt(searchParams.get('findingsMax') || '0', 10) : undefined
  );
  const [selectedOrdering, setSelectedOrdering] = React.useState<string>(searchParams.get('ordering') || 'risk_score_order');
  const [selectedTags, setSelectedTags] = React.useState<string[]>(
    searchParams.getAll('tag') || []
  );
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(
    searchParams.getAll('region') || []
  );
  const [domains, setDomains] = React.useState<Domain[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = React.useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });

  // Update local state when search parameters change (for back button navigation)
  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const typeParam = searchParams.get('type');
    const statusParam = searchParams.get('status');
    const severityParam = searchParams.get('severity');
    const timeframeParam = searchParams.get('timeframe');
    const tagParams = searchParams.getAll('tag');
    const regionParams = searchParams.getAll('region');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update type if it changed
    if (typeParam !== selectedType) {
      setSelectedType(typeParam || 'all');
    }
    
    // Update status if it changed
    if (statusParam !== selectedStatus) {
      setSelectedStatus(statusParam || 'all');
    }
    
    // Update severity if it changed
    if (severityParam !== selectedSeverity) {
      setSelectedSeverity(severityParam || 'all');
    }
    
    // Update timeframe if it changed
    if (timeframeParam !== selectedTimeframe) {
      setSelectedTimeframe(timeframeParam || 'all');
    }
    
    // Update tags if they changed
    if (JSON.stringify(tagParams) !== JSON.stringify(selectedTags)) {
      setSelectedTags(tagParams || []);
    }
    
    // Update regions if they changed
    if (JSON.stringify(regionParams) !== JSON.stringify(selectedRegions)) {
      setSelectedRegions(regionParams || []);
    }
  }, [searchParams]);

  const fetchDomains = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      // console.log('Fetching domains with params:', {
      //   teamSlug: currentTeam.slug,
      //   page: currentPage,
      //   search: searchQuery
      // });
      // Prepare API request with all filters
      const requestParams: DomainsListRequest = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        ordering: selectedOrdering,
      };
      
      // Add risk score filters if selected (ignoring 'no_filter' values)
      if (selectedRiskScoreMin && selectedRiskScoreMin !== 'no_filter') {
        requestParams.riskScore = selectedRiskScoreMin;
      }
      // if (selectedRiskScoreMax && selectedRiskScoreMax !== 'no_filter') {
      //   requestParams.riskScoreMax = selectedRiskScoreMax;
      // }
      
      // Add findings count filters if selected
      if (selectedFindingsMin !== undefined && selectedFindingsMin > 0) {
        requestParams.findingsMin = selectedFindingsMin;
      }
      if (selectedFindingsMax !== undefined && selectedFindingsMax > 0) {
        requestParams.findingsMax = selectedFindingsMax;
      }
      
      console.log('Fetching domains with params:', requestParams);
      const response = await api.domainsList(requestParams);
      console.log('API Response:', response);
      setDomains(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentTeam?.slug,
    token,
    currentPage,
    searchQuery,
    selectedRiskScoreMin,
    selectedFindingsMin,
    selectedFindingsMax,
    selectedOrdering
  ]);

  const fetchAllDomains = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new AssetsApi(getApiConfiguration(token));
    const response = await api.domainsList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      ordering: selectedOrdering,
      limit
    });
    return response.results;
  };

  React.useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  // Helper function to build URL with search parameters
  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add type if not 'all'
    if (params.type && params.type !== 'all') {
      urlParams.set('type', params.type);
    }
    
    // Add status if not 'all'
    if (params.status && params.status !== 'all') {
      urlParams.set('status', params.status);
    }
    
    // Add severity if not 'all'
    if (params.severity && params.severity !== 'all') {
      urlParams.set('severity', params.severity);
    }
    
    // Add timeframe if not 'all'
    if (params.timeframe && params.timeframe !== 'all') {
      urlParams.set('timeframe', params.timeframe);
    }
    
    // Add tags if present
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => urlParams.append('tag', tag));
    }
    
    // Add regions if present
    if (params.regions && params.regions.length > 0) {
      params.regions.forEach((region: string) => urlParams.append('region', region));
    }
    
    // Add risk score if present
    if (params.riskScore) {
      urlParams.set('riskScore', params.riskScore);
    }
    
    // Add findings min if present
    if (params.findingsMin !== undefined) {
      urlParams.set('findingsMin', params.findingsMin.toString());
    }
    
    // Add findings max if present
    if (params.findingsMax !== undefined) {
      urlParams.set('findingsMax', params.findingsMax.toString());
    }
    
    // Add ordering if present
    if (params.ordering) {
      urlParams.set('ordering', params.ordering);
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };

  // Handle page change with URL param update
  const handlePageChange = (page: number) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
    
    // Update the local state
    setCurrentPage(page);
  };

  // Handle search change with URL param update
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: query,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle type change
  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle status change
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle severity change
  const handleSeverityChange = (severity: string) => {
    setSelectedSeverity(severity);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle timeframe change
  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle tag change
  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tag] 
      : selectedTags.filter(t => t !== tag);
      
    setSelectedTags(newTags);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: newTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle region change
  const handleRegionChange = (region: string, checked: boolean) => {
    const newRegions = checked 
      ? [...selectedRegions, region] 
      : selectedRegions.filter(r => r !== region);
      
    setSelectedRegions(newRegions);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: newRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle risk score min changes
  const handleRiskScoreMinChange = (value: string) => {
    setSelectedRiskScoreMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: value,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings min changes
  const handleFindingsMinChange = (value: number | undefined) => {
    setSelectedFindingsMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: value,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings max changes
  const handleFindingsMaxChange = (value: number | undefined) => {
    setSelectedFindingsMax(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: value,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle ordering changes
  const handleOrderingChange = (value: string) => {
    setSelectedOrdering(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      severity: selectedSeverity,
      timeframe: selectedTimeframe,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: value
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle filter reset
  const handleReset = () => {
    // Navigate to base URL without parameters
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
    
    // Reset all local state
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedSeverity('all');
    setSelectedRiskScoreMin('');
    setSelectedFindingsMin(undefined);
    setSelectedFindingsMax(undefined);
    setSelectedTimeframe('all');
    setSelectedTags([]);
    setSelectedRegions([]);
    setCurrentPage(1);
  };

  // Transform API data for the table
  const transformedDomains = domains.map((domain) => {
    // Parse findingsStats from JSON string to object
    const findingsStats = parseJsonData<FindingsStats>(domain.findingsStats);
    const technologies = parseJsonData(domain.technologies) || [];

    return {
      id: domain.id.toString(),
      name: domain.domainName || 'Unknown',
      type: 'web',
      status: domain.isActive ? 'active' : 'inactive',
      riskScore: (domain.riskScore || 'n/a').toUpperCase(),
      updatedAt: new Date(domain.updatedAt).toLocaleString(),
      tags: domain.tags || [],
      vulnerabilities: {
        critical: findingsStats?.critical || 0,
        high: findingsStats?.high || 0,
        medium: findingsStats?.medium || 0,
        low: findingsStats?.low || 0
      },
      technologies: JSON.parse(JSON.stringify(domain.technologies))?.map((tech: any) => ({
        name: tech.name || 'Unknown',
        version: tech.version || 'Unknown',
        logo_path: tech.logo_path || '',
        color: tech.color || 'gray'
      })) || [],
      registrar: domain.registrar,
      registrationDate: domain.registrationDate ? domain.registrationDate.toString() : null,
      expirationDate: domain.expirationDate ? domain.expirationDate.toString() : null,
      tlsCertificate: domain.tlsCertificate ? {
        sni: domain.tlsCertificate.sni,
        host: domain.tlsCertificate.host,
        port: domain.tlsCertificate.port,
        cipher: domain.tlsCertificate.cipher,
        issuer_cn: domain.tlsCertificate.issuer_cn,
        not_after: domain.tlsCertificate.not_after,
        not_before: domain.tlsCertificate.not_before,
        subject_cn: domain.tlsCertificate.subject_cn,
        tls_version: domain.tlsCertificate.tls_version,
        probe_status: domain.tlsCertificate.probe_status
      } : null,
      subdomains: {
        total: parseInt(domain.subdomainsCount || '0', 10),
        active: Math.floor(Math.random() * 15) + 3, // Random number between 3 and 18
        inactive: Math.floor(Math.random() * 7) + 2 // Random number between 2 and 9
      }
    };
  });

  // Metrics for domains page - using count from API response
  const domainMetrics = {
    web: domains.length,
    servers: 0,
    databases: 0,
    network: 0,
    vulnerable: domains.filter((d) => !d.isActive).length,
    secure: domains.filter((d) => d.isActive).length,
    total: domains.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Globe className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Domains</h1>
            <p className="text-sm text-gray-400">Manage and monitor your domain assets</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* <ExportButton onClick={() => console.log('Exporting domains...')} /> */}
          <ExportActionBtn
            fetchAllData={() => fetchAllDomains(paginationInfo.count)}
            filename="domains"
            disabled={domains?.length === 0}
          />
          <AddAssetButton onClick={() => console.log('Adding new domain...')} />
        </div>
      </div>

      {/* Metrics Overview 
      <AssetsMetrics metrics={domainMetrics} />
*/}
      {/* Tabs */}
      <AssetTabs />

      {/* Filters */}
      <AssetsFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedSeverity={selectedSeverity}
        onSeverityChange={handleSeverityChange}
        selectedRiskScoreMin={selectedRiskScoreMin}
        onRiskScoreMinChange={handleRiskScoreMinChange}
        selectedFindingsMin={selectedFindingsMin}
        onFindingsMinChange={handleFindingsMinChange}
        selectedFindingsMax={selectedFindingsMax}
        onFindingsMaxChange={handleFindingsMaxChange}
        selectedOrdering={selectedOrdering}
        onOrderingChange={handleOrderingChange}
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedRegions={selectedRegions}
        onRegionChange={handleRegionChange}
        onReset={handleReset}
        onExport={() => {
          console.log('Exporting domains...');
        }}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading domains</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <DomainsTable
          domains={transformedDomains}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalDomains={paginationInfo.count}
          itemsPerPage={10}
          paginationInfo={paginationInfo}
        />
      )}
    </div>
  );
};

export default Domains;